import React, { useState } from "react";
import styled from "styled-components";
import { Modal, Form, Input, Button, Divider, message, Alert } from "antd";
import { useMealMate } from "../../context/MealMateContext";
import { api, endpoints } from "../../utils/axiosInstance";
import { signInWithPopup } from "firebase/auth";
import { auth, provider } from "../../utils/firebase";

const ModalSignInComponent = ({ isOpen, onClose }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [isSignUp, setIsSignUp] = useState(false);
  const { register, setUser, setIsAuthenticated, setShowLoginModal } =
    useMealMate();

  const handleGoogleLogin = async () => {
    try {
      setLoading(true);
      setLoginError("");

      // 1. Đăng nhập với Google thông qua Firebase
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // 2. Lấy thông tin cần thiết từ user Google
      const userData = {
        email: user.email,
        full_name: user.displayName,
        picture: user.photoURL,
      };

      // 3. Gọi API để đăng ký/đăng nhập user với backend
      const response = await api.post(endpoints.auth.loginWithGoogle, userData);

      // 4. Xử lý response từ backend
      if (response && response.accessToken) {
        // Lưu token
        sessionStorage.setItem("token", response.accessToken);

        // Cập nhật thông tin user trong context
        const userInfo = {
          role: response.role,
          full_name: response.full_name,
          email: response.email,
          profile_picture: response.profile_picture,
          gender: response.gender,
          phone: response.phone,
          authProvider: response.authProvider,
        };

        // Cập nhật state
        setUser(userInfo);
        setIsAuthenticated(true);
        setShowLoginModal(false);
        message.success("Đăng nhập với Google thành công!");
        onClose();
      } else {
        throw new Error("Không nhận được dữ liệu hợp lệ từ server");
      }
    } catch (error) {
      console.error("Google login error:", error);
      let errorMessage = "Đăng nhập bằng Google thất bại. Vui lòng thử lại.";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setLoginError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      setLoginError(""); // Clear previous error
      if (isSignUp) {
        const response = await api.post(endpoints.auth.register, {
          email: values.email,
          password: values.password,
          full_name: values.full_name,
          phone: values.phone,
        });

        if (response && response.accessToken) {
          const userInfo = {
            _id: response._id,
            full_name: response.full_name,
            email: response.email,
            phone: response.phone,
            role: response.role,
          };

          register(userInfo, response.accessToken);
          message.success("Đăng ký thành công!");
          onClose();
        } else {
          throw new Error("Không nhận được dữ liệu đăng ký hợp lệ từ server");
        }
      } else {
        // Xử lý đăng nhập
        const response = await api.post(endpoints.auth.userLogin, {
          email: values.email,
          password: values.password,
        });

        if (response && response.accessToken) {
          const userInfo = {
            _id: response._id,
            full_name: response.full_name,
            email: response.email,
            phone: response.phone,
            role: response.role,
          };

          // Lưu thông tin và token
          sessionStorage.setItem("token", response.accessToken);
          setUser(userInfo);
          setIsAuthenticated(true);
          setShowLoginModal(false);
          message.success("Đăng nhập thành công!");
          onClose();
        } else {
          throw new Error("Không nhận được dữ liệu đăng nhập hợp lệ từ server");
        }
      }
    } catch (error) {
      console.error("Auth error:", error);
      let errorMessage = isSignUp
        ? "Đăng ký không thành công. Vui lòng thử lại."
        : "Email hoặc mật khẩu không đúng. Vui lòng thử lại.";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setLoginError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setIsSignUp(!isSignUp);
    setLoginError("");
    form.resetFields();
  };

  const handleForgotPassword = async () => {
    try {
      const email = form.getFieldValue("email");
      if (!email) {
        return message.error("Vui lòng nhập email của bạn");
      }

      setLoading(true);
      await api.post(endpoints.auth.forgotPassword, { email });
      message.success(
        "Hướng dẫn đặt lại mật khẩu đã được gửi đến email của bạn"
      );
    } catch (error) {
      console.error("Forgot password error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={400}
      centered
      title={
        <ModalHeader>
          <Title>{isSignUp ? "Đăng Ký" : "Đăng Nhập"}</Title>
          <SubTitle>
            {isSignUp
              ? "Tạo tài khoản mới tại Mealmate!"
              : "Chào mừng bạn trở lại với Mealmate!"}
          </SubTitle>
        </ModalHeader>
      }
    >
      <StyledForm form={form} layout="vertical" onFinish={handleSubmit}>
        {loginError && (
          <Form.Item>
            <Alert
              message={loginError}
              type="error"
              showIcon
              style={{ marginBottom: "16px" }}
            />
          </Form.Item>
        )}

        {isSignUp && (
          <Form.Item
            name="full_name"
            label="Họ và tên"
            rules={[
              { required: true, message: "Vui lòng nhập họ và tên" },
              { max: 50, message: "Họ và tên không được quá 50 ký tự" },
            ]}
          >
            <Input
              placeholder="Nhập họ và tên"
              size="large"
              autoComplete="name"
            />
          </Form.Item>
        )}

        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: "Vui lòng nhập email" },
            { type: "email", message: "Email không hợp lệ" },
            { max: 50, message: "Email không được quá 50 ký tự" },
          ]}
        >
          <Input
            placeholder="Nhập email"
            size="large"
            autoComplete="username"
          />
        </Form.Item>

        <Form.Item
          name="password"
          label="Mật khẩu"
          rules={[
            { required: true, message: "Vui lòng nhập mật khẩu" },
            { min: 6, message: "Mật khẩu phải có ít nhất 6 ký tự" },
            { max: 50, message: "Mật khẩu không được quá 50 ký tự" },
          ]}
        >
          <Input.Password
            placeholder="Nhập mật khẩu"
            size="large"
            autoComplete="current-password"
          />
        </Form.Item>

        {isSignUp && (
          <Form.Item
            name="confirm_password"
            label="Xác nhận mật khẩu"
            dependencies={["password"]}
            rules={[
              { required: true, message: "Vui lòng xác nhận mật khẩu" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("Mật khẩu xác nhận không khớp")
                  );
                },
              }),
            ]}
          >
            <Input.Password
              placeholder="Xác nhận mật khẩu"
              size="large"
              autoComplete="new-password"
            />
          </Form.Item>
        )}

        {isSignUp && (
          <Form.Item
            name="phone"
            label="Số điện thoại"
            rules={[
              { required: true, message: "Vui lòng nhập số điện thoại" },
              { pattern: /^[0-9]{10}$/, message: "Số điện thoại không hợp lệ" },
            ]}
          >
            <Input
              placeholder="Nhập số điện thoại"
              size="large"
              autoComplete="tel"
            />
          </Form.Item>
        )}

        {!isSignUp && (
          <ForgotPassword onClick={handleForgotPassword}>
            Quên mật khẩu?
          </ForgotPassword>
        )}

        <Form.Item>
          <SignInButton
            type="primary"
            htmlType="submit"
            block
            size="large"
            loading={loading}
            disabled={loading}
          >
            {isSignUp ? "Đăng Ký" : "Đăng Nhập"}
          </SignInButton>
        </Form.Item>

        {!isSignUp && (
          <>
            <StyledDivider>hoặc</StyledDivider>
            <Form.Item>
              <GoogleButton
                block
                size="large"
                onClick={handleGoogleLogin}
                loading={loading}
              >
                <GoogleIcon>
                  <svg viewBox="0 0 24 24" width="24" height="24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                </GoogleIcon>
                Tiếp tục với Google
              </GoogleButton>
            </Form.Item>
          </>
        )}

        <SwitchModeText>
          {isSignUp ? "Đã có tài khoản? " : "Chưa có tài khoản? "}
          <SwitchModeLink onClick={switchMode}>
            {isSignUp ? "Đăng nhập" : "Đăng ký"}
          </SwitchModeLink>
        </SwitchModeText>
      </StyledForm>
    </Modal>
  );
};

const ModalHeader = styled.div`
  text-align: center;
  margin-bottom: 0.5rem;
`;

const Title = styled.h2`
  font-size: 1.8rem;
  color: #333;
  margin-bottom: 0.5rem;
  text-align: center;
`;

const SubTitle = styled.p`
  color: #666;
  font-size: 1rem;
  text-align: center;
  margin: 0;
`;

const StyledForm = styled(Form)`
  padding: 1rem 0;
`;

const ForgotPassword = styled.a`
  display: block;
  text-align: right;
  color: #ff4b4b;
  margin-top: -0.5rem;
  margin-bottom: 1rem;
  font-size: 0.9rem;

  &:hover {
    text-decoration: underline;
  }
`;

const SignInButton = styled(Button)`
  background: #ff4b4b;
  border-color: #ff4b4b;
  height: 45px;
  font-weight: 500;

  &:hover,
  &:focus {
    background: #ff3333 !important;
    border-color: #ff3333 !important;
  }
`;

const StyledDivider = styled(Divider)`
  font-size: 0.9rem;
  color: #666;
  margin: 1.5rem 0;
`;

const GoogleButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 45px;
  font-weight: 500;
  gap: 0.8rem;

  &:hover {
    color: #333;
    border-color: #d9d9d9;
  }
`;

const GoogleIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SwitchModeText = styled.p`
  text-align: center;
  color: #666;
  font-size: 0.9rem;
  margin-top: 1rem;
  margin-bottom: 0;
`;

const SwitchModeLink = styled.a`
  color: #ff4b4b;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

export default ModalSignInComponent;
